import { getIsStatus, toPostStatusEnum } from '@/Components/posts/status';
import type { PickFromPostCardTypes } from '@/Components/posts/ui/posts-card';
import { useAuthStore } from '@/Utility/auth/Auth';
import type { Znglist_Query$data } from '@/graphql/queries/Znglist.query';
import { PostStatusEnum, type ZnglyVideo } from '@/graphql/types';
import { type AppStore, useAppStore } from '@/inertia/AppStore';
import isDeepEqual from 'fast-deep-equal';
import { throttle } from 'lodash';
import { useMemo } from 'react';
import type { DeepNullable, DeepPartial, DeepPick, DeepWritable } from 'ts-essentials';
import { type TemporalState, temporal } from 'zundo';
import { create } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { getZnglistTemplateInfo } from './template/DefaultSelectTemplate';

type EditZnglistData = Znglist_Query$data['znglist'];
type ZnglistType = DeepWritable<EditZnglistData>;

type CatalogItem =
  | NonNullable<NonNullable<NonNullable<ZnglistType>['znglist']>['znglyContents']>[number]
  | PickFromPostCardTypes<'catalog'>['node']
  | ZnglyVideo;

type Actions = {
  setData: (data?: ZnglistType | EditZnglistData) => void;
  addCatalogItem: (item: CatalogItem) => boolean;
  deleteCatalogItem: (item: CatalogItem | DeepPick<CatalogItem, { id: never }>) => boolean;
  setCatalogItems: (items: CatalogItem[]) => void;
};

const initRelayConfig = {
  __config: {
    isFallback: false,
    viewAs: 'postAuthor' as 'currentUser' | 'postAuthor',
  },
};

const initZnglistStore: Partial<ZnglistType> = {
  id: '' as string,
  title: '' as string,
  status: '' as PostStatusEnum,
  znglist: {
    form: null,
    order: null,
    templateType: null,
    userType: null,
    znglyContents: [],
  },
};

export type EditZnglistStore = DeepPartial<ZnglistType> & typeof initRelayConfig & Actions;

export const useEditZnglistStore = create<EditZnglistStore>()(
  temporal(
    (set) => ({
      ...initZnglistStore,
      ...initRelayConfig,
      setData: (page) => {
        set((prev) => {
          if (!page) {
            return nullifyStoreData(prev, initZnglistStore);
          }

          return {
            ...(page as ZnglistType),
            status: toPostStatusEnum(page?.status),
            znglist: {
              ...page?.znglist,
              templateType: getZnglistTemplateInfo(page?.znglist?.templateType)?.type,
            } as NonNullable<ZnglistType>['znglist'],
          };
        });
      },
      addCatalogItem(item) {
        if (!item?.id) {
          return false;
        }

        let res = false;
        set((prev) => {
          const contents = prev?.znglist?.znglyContents ?? [];
          if (contents?.findIndex((i) => i?.id === item.id) !== -1) {
            return prev;
          }

          res = true;
          return {
            ...prev,
            znglist: {
              ...prev?.znglist,
              znglyContents: [...contents, item] as NonNullable<NonNullable<ZnglistType>['znglist']>['znglyContents'],
            },
          };
        });

        if (res) {
          // lets add in the app store as well
          useAppStore.setState({
            playerNode: item as AppStore['playerNode'],
          });
        }

        return res;
      },
      deleteCatalogItem(item) {
        if (!item?.id) {
          return false;
        }

        let res = false;
        let playerNode = null as AppStore['playerNode'] | null;
        set((prev) => {
          if (!prev?.znglist?.znglyContents?.length) {
            return prev;
          }

          const foundIdx = prev?.znglist?.znglyContents?.findIndex((i) => i?.id === item.id);
          if (foundIdx === -1) {
            return prev;
          }
          res = true;

          const newNodes = [...(prev?.znglist?.znglyContents ?? [])];
          newNodes.splice(foundIdx, 1);

          // infer the player node from the found index position in the array
          playerNode =
            (newNodes?.[foundIdx] as AppStore['playerNode']) ??
            (newNodes?.[foundIdx - 1] as AppStore['playerNode']) ??
            null;

          return {
            ...prev,
            znglist: {
              ...prev?.znglist,
              znglyContents: prev?.znglist?.znglyContents?.filter((i) => i?.id !== item.id) ?? [],
            },
          };
        });

        if (res) {
          setTimeout(() => {
            useAppStore.getState().setPlayerNode(playerNode);
          }, 250);
        }

        return res;
      },
      setCatalogItems(items) {
        set((prev) => ({
          ...prev,
          znglist: {
            ...prev?.znglist,
            znglyContents: items as NonNullable<NonNullable<ZnglistType>['znglist']>['znglyContents'],
          },
        }));
      },
    }),
    {
      equality: isDeepEqual,
      limit: 100,
      partialize(state) {
        const config: Partial<EditZnglistStore> = {};
        // partialize the state to avoid __config in temporal state
        const { __config, ...rest } = state;

        for (const key in rest) {
          // @ts-ignore
          if (typeof rest[key as keyof EditZnglistStore] === 'function') {
            continue;
          }

          // @ts-ignore
          config[key as keyof EditZnglistStore] = rest[key as keyof EditZnglistStore];
        }

        return config as EditZnglistStore;
      },
      handleSet(handleSet) {
        return throttle(handleSet, 1000);
      },
    }
  )
);

const nullifyStoreData = (prev: EditZnglistStore, initData: typeof initZnglistStore): Partial<EditZnglistStore> => {
  const newState = {} as DeepNullable<Partial<EditZnglistStore>>;

  for (const key in prev) {
    const keyValue = prev?.[key as keyof EditZnglistStore];

    if (key === '__config') {
      continue;
    }
    if (typeof keyValue === 'function') {
      continue;
    }

    newState[key as keyof EditZnglistStore] = null;
  }

  return {
    ...(newState as Partial<EditZnglistStore>),
    ...initData,
  };
};

export const useEditZnglistStoreTemporal = <T>(selector: (state: TemporalState<EditZnglistStore>) => T) =>
  useStoreWithEqualityFn(useEditZnglistStore.temporal, selector, (pastState, currentState) =>
    isDeepEqual(pastState, currentState)
  );

/**
 * Whether the Edit Znglist UI should be disabled
 */
export const useIsUiDisabled = () => {
  const fallback = useEditZnglistStore((state) => state?.__config?.isFallback);
  const status = useEditZnglistStore((state) => state?.status);
  const id = useEditZnglistStore((state) => state?.id);
  const template = useEditZnglistStore((state) => state?.znglist?.templateType);

  const templateObj = useMemo(() => getZnglistTemplateInfo(template), [template]);

  return useMemo(() => {
    if (fallback) {
      return true;
    }

    if (!id) {
      return true;
    }

    if (!templateObj?.type || templateObj?.type === undefined) {
      return true;
    }

    if (getIsStatus(status, PostStatusEnum.Trash)) {
      return true;
    }

    return false;
  }, [fallback, status, id, template]);
};

/**
 * Whether the Edit Znglist can be saved in the current state
 */
export const useCanSaveZnglist = () => {
  const id = useEditZnglistStore((state) => state?.id);
  const status = useEditZnglistStore((state) => state?.status);
  const title = useEditZnglistStore((state) => state?.title);

  const result = useMemo(() => {
    if (!id) {
      return {
        canSave: false,
        errors: [
          {
            message: 'Please select a template',
            field: 'template',
          },
        ],
      };
    }
    if (getIsStatus(status, PostStatusEnum.Trash)) {
      return {
        canSave: false,
        errors: [
          {
            message: 'Cannot save a Znglist that is in the trash. Please restore the Znglist first.',
            field: 'status',
          },
        ],
      };
    }

    const errors = [];

    if (!title) {
      errors.push({
        message: 'Please enter a title',
        field: 'title',
      });
    }

    if (errors.length) {
      return {
        canSave: false,
        errors,
      };
    }

    return {
      canSave: true,
      errors,
    };
  }, [id, status, title]);

  return result;
};

export const getZnglistPersonalLink = (viewAs?: EditZnglistStore['__config']['viewAs']) => {
  const data = useEditZnglistStore.getState();

  const viewType = viewAs ?? data?.__config?.viewAs;

  if (!data?.id) {
    return '';
  }

  if (viewType === 'postAuthor') {
    const author = data?.author?.node?.slug;

    return `/users/${author}/znglist/${data?.slug}`;
  }

  if (viewType === 'currentUser') {
    const user = useAuthStore.getState().user;

    const userSlug = user?.user?.slug;

    return `/users/${userSlug}/znglist/${data?.slug}`;
  }

  return '';
};
